import { useState } from 'react';
import { Dialog } from '@/ui/Dialog';
import { Button } from '@/ui/Button';
import { Field, Input } from '@/ui/Field';
import { useCambiarContrasena } from './api';

/**
 * CambiarContrasena — de los pocos casos que SÍ merecen un diálogo.
 *
 * Interrumpe a propósito: es una operación de seguridad, corta, que no se
 * reanuda a medias y durante la cual lo que hay detrás no importa. Ese es
 * exactamente el criterio que separa un diálogo de un panel.
 *
 * NO cubre el cambio obligatorio del primer ingreso. Ese lo dispara el shell
 * (`app.js` → `ClavePrimerIngreso.abrir()`, en
 * `js/modules/clave-primer-ingreso.js`) y sigue usando el modal vanilla, que es
 * lo correcto mientras el shell no se migre: es una barrera del arranque de
 * sesión, no una acción de esta pantalla, y moverla aquí ataría el primer
 * inicio de sesión de todo el mundo a que una pantalla React haya montado bien.
 *
 * La validación ocurre al SALIR de cada campo, no al pulsar Enviar. Enterarse de
 * que la contraseña era corta después de escribirla tres veces es el patrón que
 * hace que la gente odie los formularios.
 */
export function CambiarContrasena({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const cambiar = useCambiarContrasena();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [tocado, setTocado] = useState<Record<string, boolean>>({});
  const [errorServidor, setErrorServidor] = useState<string | null>(null);

  const errores = {
    actual: !actual ? 'Escribe tu contraseña actual.' : undefined,
    nueva:
      !nueva ? 'Escribe la contraseña nueva.'
        : nueva.length < 8 ? 'Necesita al menos 8 caracteres.'
        : nueva === actual ? 'Tiene que ser distinta de la actual.'
        : undefined,
    confirmar:
      !confirmar ? 'Repite la contraseña nueva.'
        : confirmar !== nueva ? 'No coincide con la contraseña nueva.'
        : undefined,
  };
  const valido = !errores.actual && !errores.nueva && !errores.confirmar;

  function cerrar() {
    setActual(''); setNueva(''); setConfirmar('');
    setTocado({}); setErrorServidor(null);
    onCerrar();
  }

  async function enviar() {
    setTocado({ actual: true, nueva: true, confirmar: true });
    if (!valido) return;
    setErrorServidor(null);
    try {
      await cambiar.mutateAsync({ actual, nueva });
      Utils.toast('Contraseña actualizada', 'success');
      cerrar();
    } catch (e) {
      // El error del servidor es casi siempre «la contraseña actual no es
      // correcta», y su sitio es junto al campo, no en un aviso flotante que
      // desaparece a los tres segundos mientras el usuario sigue mirando.
      setErrorServidor(e instanceof Error ? e.message : 'No se pudo cambiar la contraseña');
    }
  }

  return (
    <Dialog
      abierto={abierto}
      onCambio={(v) => { if (!v) cerrar(); }}
      titulo="Cambiar contraseña"
      descripcion="Necesitas la actual para confirmar que eres tú. La nueva debe tener al menos 8 caracteres."
      ancho="max-w-md"
      pie={
        <>
          <Button variant="ghost" onClick={cerrar}>Cancelar</Button>
          <Button loading={cambiar.isPending} onClick={() => void enviar()}>
            Cambiar contraseña
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 pt-1">
        <Field
          label="Contraseña actual"
          error={errorServidor ?? (tocado.actual ? errores.actual : undefined)}
        >
          <Input
            type="password"
            autoComplete="current-password"
            value={actual}
            onChange={(e) => { setActual(e.target.value); setErrorServidor(null); }}
            onBlur={() => setTocado((t) => ({ ...t, actual: true }))}
          />
        </Field>

        <Field
          label="Contraseña nueva"
          ayuda="Mínimo 8 caracteres."
          error={tocado.nueva ? errores.nueva : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
            onBlur={() => setTocado((t) => ({ ...t, nueva: true }))}
          />
        </Field>

        <Field
          label="Repite la contraseña nueva"
          error={tocado.confirmar ? errores.confirmar : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={confirmar}
            onChange={(e) => setConfirmar(e.target.value)}
            onBlur={() => setTocado((t) => ({ ...t, confirmar: true }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void enviar(); } }}
          />
        </Field>
      </div>
    </Dialog>
  );
}
